import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FaBuilding, 
  FaUsers, 
  FaLightbulb, 
  FaHeart, 
  FaAward, 
  FaHandshake, 
  FaShieldAlt,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaChartLine,
  FaRocket
} from 'react-icons/fa';
import { FunctionComponent } from 'react';

interface CompanyDetailsProps {}

const CompanyDetails: FunctionComponent<CompanyDetailsProps> = () => {
  const [currentYear] = useState(new Date().getFullYear());


  const FaUsersIcon = FaUsers as FunctionComponent<{ className?: string }>;
  const FaAwardIcon = FaAward as FunctionComponent<{ className?: string }>;
  const FaChartLineIcon = FaChartLine as FunctionComponent<{ className?: string }>;
  const FaBuildingIcon = FaBuilding as FunctionComponent<{ className?: string }>;
  const FaHeartIcon = FaHeart as FunctionComponent<{ className?: string }>;
  const FaLightbulbIcon = FaLightbulb as FunctionComponent<{ className?: string }>;
  const FaHandshakeIcon = FaHandshake as FunctionComponent<{ className?: string }>;
  const FaShieldAltIcon = FaShieldAlt as FunctionComponent<{ className?: string }>;
  const FaEnvelopeIcon = FaEnvelope as FunctionComponent<{ className?: string }>;
  const FaPhoneIcon = FaPhone as FunctionComponent<{ className?: string }>;
  const FaMapMarkerAltIcon = FaMapMarkerAlt as FunctionComponent<{ className?: string }>;
  const FaFacebookIcon = FaFacebook as FunctionComponent<{ className?: string }>;
  const FaTwitterIcon = FaTwitter as FunctionComponent<{ className?: string }>;
  const FaInstagramIcon = FaInstagram as FunctionComponent<{ className?: string }>;
  const FaRocketIcon = FaRocket as FunctionComponent<{ className?: string }>;


  const values = [
    {
      icon: FaHeartIcon,
      title: 'Health First',
      description: 'We believe true health comes from returning to ancestral eating patterns that nourish the human body at its deepest level.'
    },
    {
      icon: FaLightbulbIcon,
      title: 'Innovation',
      description: 'Combining ancient wisdom with modern technology to create tools that empower lasting health transformations.'
    },
    {
      icon: FaHandshakeIcon,
      title: 'Community',
      description: 'Building a supportive global community where people can share their health journeys and inspire each other.'
    },
    {
      icon: FaShieldAltIcon,
      title: 'Transparency',
      description: 'We operate with complete honesty about our methods, ingredients, and the science behind carnivore nutrition.'
    }
  ];

  const teamMembers = [
    {
      name: 'Bilal Haider',
      role: 'Co-Founder & Community Builder',
      bio: 'Built the world\'s largest carnivore communities and leads our global outreach efforts with data-driven insights.',
      avatar: 'BH'
    },
    {
      name: 'Rie Sugisawa',
      role: 'Co-Founder & Health Advocate',
      bio: 'Transformed personal health crisis into a mission to help others heal through ancestral nutrition and wellness.',
      avatar: 'RS'
    }
  ];

  const milestones = [
    { year: '2023', event: 'OdinVistra LLC Founded', description: 'Bilal Haider and Rie Sugisawa unite to create a health revolution' },
    { year: '2023', event: 'EatMeat App Launch', description: 'First comprehensive carnivore diet tracking app released' },
    { year: '2024', event: '10,000 Users', description: 'Reached our first major user milestone' },
    { year: '2024', event: '50,000+ Community', description: 'Built the largest digital carnivore community worldwide' },
    { year: currentYear.toString(), event: 'Global Expansion', description: 'Expanding our impact to help millions reclaim their health' }
  ];

  return (
    <div className="bg-background min-h-screen">
      <Helmet>
        <title>About OdinVistra LLC - The Company Behind EatMeat App | Carnivore Diet Leaders</title>
        <meta
          name="description"
          content="Learn about OdinVistra LLC, the innovative company behind the #1 carnivore diet app EatMeat. Discover our mission, values, and the inspiring story of our founders Jordan and Bilal."
        />
        <meta
          name="keywords"
          content="OdinVistra LLC, EatMeat app company, carnivore diet company, Jordan Bilal founders, meat-based nutrition company, ancestral health company"
        />
      </Helmet>

      {/* Company Overview */}
      <section className="relative overflow-hidden py-24 md:py-32 bg-gradient-to-b from-white via-orange-50/40 to-white">
        {/* Ambient glow blobs */}
        <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-50" />
        <div className="pointer-events-none absolute top-20 -right-32 w-[28rem] h-[28rem] bg-accent/20 rounded-full blur-3xl opacity-40" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-80 h-80 bg-amber-200 rounded-full blur-3xl opacity-30" />

        {/* Subtle dot grid */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden="true"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-1.5 bg-white border border-primary/30 rounded-full text-primary font-bold text-xs uppercase tracking-widest mb-6 shadow-sm">
              <FaBuildingIcon className="mr-2 h-3.5 w-3.5" />
              Our Story
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-[1.05] tracking-tight">
              The story of <br className="hidden md:block" />
              <span className="bg-gradient-to-r from-primary via-orange-500 to-accent bg-clip-text text-transparent">OdinVistra</span>
            </h1>
            <p className="text-lg md:text-xl text-text/70 max-w-3xl mx-auto leading-relaxed">
              Two health transformation stories that converged to create a global movement toward ancestral nutrition and optimal human health.
            </p>
          </div>
            
          {/* Enhanced Founder Stories */}
          <div className="grid lg:grid-cols-2 gap-12 mb-24">
            {/* Rie's Story */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-primary/10 relative overflow-hidden group hover:shadow-3xl transition-all duration-500">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg">
                  RS
                </div>
                <h3 className="text-3xl font-bold text-text mb-6 group-hover:text-primary transition-colors duration-300">
                  Rie Sugisawa's Journey
                </h3>
                <div className="space-y-5 text-text/80 leading-relaxed">
                  <p className="text-lg font-medium text-primary">From Chronic Illness to Radical Health</p>
                  <p>In my 30s, I was deeply unwell. I battled chronic symptoms—hormonal imbalances, fatigue, brain fog, inflammation, gut issues, anxiety.</p>
                  <p>I tried everything: veganism, supplements, functional medicine, biohacking. But nothing lasted.</p>
                  <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-6 border-l-4 border-primary my-6">
                    <div className="flex items-start space-x-4">
                      <FaHeartIcon className="text-primary h-6 w-6 mt-1 flex-shrink-0" />
                      <p className="text-text font-semibold italic">
                        "Within weeks of eating only animal-based foods, my body began healing in ways no doctor had ever made possible. My hormones stabilized. My brain lit up. My energy returned."
                      </p>
                    </div>
                  </div>
                  <p className="font-medium text-text">For the first time in years, I felt like myself again—clear, strong, and alive.</p>
                </div>
              </div>
            </div>

            {/* Bilal's Story */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-primary/10 relative overflow-hidden group hover:shadow-3xl transition-all duration-500">
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full -translate-y-16 -translate-x-16"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-accent to-primary rounded-2xl flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg">
                  BH
                </div>
                <h3 className="text-3xl font-bold text-text mb-6 group-hover:text-primary transition-colors duration-300">
                  Bilal Haider's Vision
                </h3>
                <div className="space-y-5 text-text/80 leading-relaxed">
                  <p className="text-lg font-medium text-primary">Community Builder & Health Pioneer</p>
                  <p>Bilal had already built some of the largest carnivore diet communities in the world—connecting thousands of people reclaiming their health through ancestral eating.</p>
                  <p>Driven by data, history, and real results, he witnessed the transformative power of returning to how humans have eaten for over 2 million years.</p>
                  <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-2xl p-6 border-l-4 border-accent my-6">
                    <div className="flex items-start space-x-4">
                      <FaUsersIcon className="text-accent h-6 w-6 mt-1 flex-shrink-0" />
                      <p className="text-text font-semibold italic">
                        "When people remove modern processed foods and return to ancestral eating, they don't just get better. They transform."
                      </p>
                    </div>
                  </div>
                  <p className="font-medium text-text">His mission: empower millions to live healthier, freer, more purposeful lives through meat-based nutrition.</p>
                </div>
              </div>
            </div>
          </div>

          {/* The Birth of OdinVistra */}
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 mb-12 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                <FaRocketIcon className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">The Birth of OdinVistra</h3>
              <div className="space-y-4 text-text/80 leading-relaxed text-lg">
                <p>When Bilal Haider and Rie Sugisawa crossed paths, it wasn't just collaboration — it was alignment.</p>
                <p>We came from different worlds, but we had the same realization:</p>
                <blockquote className="my-6 px-6 py-5 bg-gradient-to-r from-primary/5 to-accent/5 border-l-4 border-primary rounded-r-xl italic font-semibold text-gray-800 text-xl">
                  Modern health advice is broken, and the solution isn't more noise — it's a return to ancestral wisdom.
                </blockquote>
                <p>We created OdinVistra to make that return possible for everyone. Our name stands for clarity, strength, and vision — qualities we believe every human deserves to reclaim.</p>
              </div>

              <div className="mt-8">
                <p className="font-semibold text-gray-900 mb-4">OdinVistra is more than a company. It's a movement to:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { icon: FaHeartIcon, text: 'Reverse chronic illness', color: 'text-red-500', bg: 'bg-red-50' },
                    { icon: FaLightbulbIcon, text: 'Restore metabolic & mental health', color: 'text-amber-500', bg: 'bg-amber-50' },
                    { icon: FaRocketIcon, text: 'Reignite purpose, passion & energy', color: 'text-primary', bg: 'bg-primary/10' },
                    { icon: FaHandshakeIcon, text: 'Reconnect people to ancestral truth', color: 'text-green-600', bg: 'bg-green-50' },
                  ].map((point, i) => {
                    const Icon = point.icon;
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className={`w-9 h-9 rounded-lg ${point.bg} flex items-center justify-center shrink-0`}>
                          <Icon className={`h-4 w-4 ${point.color}`} />
                        </div>
                        <span className="text-text font-medium pt-1.5">{point.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="mt-8 text-lg text-text/80 leading-relaxed">
                Because when you remove what harms the body and give it what it was built to run on — meat, fat, minerals, and sunlight — the body doesn't just survive. <strong className="text-gray-900">It thrives.</strong>
              </p>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="max-w-3xl mx-auto relative overflow-hidden rounded-[2rem] shadow-2xl"
            style={{ background: 'radial-gradient(at 20% 0%, rgba(249,115,22,0.35) 0%, transparent 50%), radial-gradient(at 80% 100%, rgba(239,68,68,0.30) 0%, transparent 50%), linear-gradient(135deg, #1f2937 0%, #111827 100%)' }}>
            {/* Ambient flame glow */}
            <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 w-80 h-80 bg-primary/30 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute -top-32 -left-32 w-80 h-80 bg-accent/30 rounded-full blur-3xl" />
            {/* Subtle grain texture via dot pattern */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.08]" aria-hidden="true"
              style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '16px 16px' }} />

            <div className="relative p-10 md:p-14 text-center text-white">
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] bg-white/10 backdrop-blur rounded-full px-3.5 py-1.5 mb-6 ring-1 ring-white/15">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                Our Mission
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold mb-8 tracking-tight">This is who we are</h3>

              <div className="space-y-6 max-w-xl mx-auto">
                <p className="text-lg text-white/70">We're not here to sell perfection.</p>
                <p className="text-2xl md:text-3xl font-extrabold leading-tight">
                  We're here to spark <span className="bg-gradient-to-r from-amber-300 via-primary to-red-400 bg-clip-text text-transparent">freedom, health,</span> and radical self-trust.
                </p>
                <p className="text-lg text-white/70">You don't need to live tired, inflamed, and confused.</p>
                <p className="text-2xl md:text-3xl font-extrabold leading-tight">
                  You were built for <span className="bg-gradient-to-r from-amber-300 via-primary to-red-400 bg-clip-text text-transparent">so much more</span>.
                </p>
              </div>

              <div className="mt-10 pt-6 border-t border-white/10 space-y-1 text-base font-semibold">
                <p className="text-white/80">Welcome to OdinVistra.</p>
                <p className="text-white/80">Welcome back to your nature.</p>
                <p className="text-xl mt-3 bg-gradient-to-r from-amber-300 via-primary to-red-400 bg-clip-text text-transparent">Let's change the world.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values Section */}
      <section className="bg-gray-50 py-20 md:py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary font-semibold text-sm mb-5">
              <FaAwardIcon className="mr-2 h-4 w-4" />
              OUR VALUES
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              What drives <span className="text-primary">everything we do</span>
            </h2>
            <p className="text-lg text-text/70">
              Our core values guide every decision we make and every product we create.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((value, index) => {
              const Icon = value.icon;
              const tints = [
                { bg: 'from-red-50 to-rose-50', icon: 'text-red-500', ring: 'ring-red-100' },
                { bg: 'from-amber-50 to-yellow-50', icon: 'text-amber-500', ring: 'ring-amber-100' },
                { bg: 'from-green-50 to-emerald-50', icon: 'text-green-600', ring: 'ring-green-100' },
                { bg: 'from-blue-50 to-sky-50', icon: 'text-blue-600', ring: 'ring-blue-100' },
              ];
              const tint = tints[index % tints.length];
              return (
                <div key={index} className={`group bg-white rounded-3xl p-6 ring-1 ${tint.ring} hover:shadow-2xl hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tint.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`h-6 w-6 ${tint.icon}`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-text/70 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-white py-20 md:py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary font-semibold text-sm mb-5">
              <FaUsersIcon className="mr-2 h-4 w-4" />
              LEADERSHIP TEAM
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              Meet the <span className="text-primary">visionaries</span>
            </h2>
            <p className="text-lg text-text/70">
              The passionate leaders behind OdinVistra's mission to transform global health.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center">
                <div className="w-24 h-24 mx-auto mb-5 rounded-3xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  {member.avatar}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <div className="text-sm font-semibold text-primary mb-3 uppercase tracking-wider">{member.role}</div>
                <p className="text-text/70 leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="bg-gray-50 py-20 md:py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary font-semibold text-sm mb-5">
              <FaChartLineIcon className="mr-2 h-4 w-4" />
              OUR JOURNEY
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              Key <span className="text-primary">milestones</span>
            </h2>
            <p className="text-lg text-text/70">
              From personal transformation to global impact — our journey continues.
            </p>
          </div>

          <div className="relative max-w-3xl mx-auto">
            {/* Vertical line */}
            <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-primary via-accent to-primary/30 md:-translate-x-1/2" aria-hidden="true" />

            <ol className="space-y-8">
              {milestones.map((milestone, index) => {
                const left = index % 2 === 0;
                return (
                  <li key={index} className="relative pl-12 md:pl-0">
                    {/* Marker */}
                    <span className="absolute left-4 md:left-1/2 top-3 w-3 h-3 -translate-x-1/2 rounded-full bg-primary ring-4 ring-white shadow-lg" />
                    <div className={`md:w-1/2 ${left ? 'md:pr-10 md:text-right' : 'md:pl-10 md:ml-auto'}`}>
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-lg transition-shadow">
                        <div className={`inline-block text-[11px] font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full mb-2 tracking-wider`}>
                          {milestone.year}
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-1">{milestone.event}</h3>
                        <p className="text-sm text-text/70 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-white py-20 md:py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary font-semibold text-sm mb-5">
              <FaEnvelopeIcon className="mr-2 h-4 w-4" />
              CONTACT US
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              Get in <span className="text-primary">touch</span>
            </h2>
            <p className="text-lg text-text/70">
              Questions, partnerships, or just want to say hi? We'd love to hear from you.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {/* HQ */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-xl transition-all">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                <FaMapMarkerAltIcon className="h-5 w-5 text-red-500" />
              </div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Headquarters</div>
              <h3 className="text-base font-bold text-gray-900 mb-2">OdinVistra LLC</h3>
              <address className="not-italic text-sm text-text/70 leading-relaxed">
                8126 Camelback Place<br />
                Pleasant Hill, CA 94523<br />
                United States
              </address>
            </div>

            {/* Email */}
            <a href="mailto:odinvistra@gmail.com"
              className="group bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FaEnvelopeIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Email</div>
              <div className="text-base font-bold text-gray-900 group-hover:text-primary transition-colors break-all">odinvistra@gmail.com</div>
              <p className="text-xs text-text/60 mt-2">Business inquiries &amp; partnerships</p>
            </a>

            {/* Phone */}
            <a href="tel:+15102245089"
              className="group bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FaPhoneIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Phone</div>
              <div className="text-base font-bold text-gray-900 group-hover:text-primary transition-colors">+1 (510) 224-5089</div>
              <p className="text-xs text-text/60 mt-2">Mon – Fri · 9 AM – 5 PM PST</p>
            </a>
          </div>

          {/* Social Media */}
          <div className="mt-14 max-w-3xl mx-auto text-center">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-5">Follow our journey</h3>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href="https://www.facebook.com/profile.php?id=61576830232558" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-full hover:border-blue-400 hover:shadow-md transition-all">
                <FaFacebookIcon className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Facebook</span>
              </a>
              <a href="https://x.com/appeatmeat" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-full hover:border-gray-900 hover:shadow-md transition-all">
                <FaTwitterIcon className="h-4 w-4 text-gray-900" />
                <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">Twitter</span>
              </a>
              <a href="https://www.instagram.com/appeatmeat/" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-full hover:border-pink-400 hover:shadow-md transition-all">
                <FaInstagramIcon className="h-4 w-4 text-pink-500" />
                <span className="text-sm font-semibold text-gray-700 group-hover:text-pink-500 transition-colors">Instagram</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyDetails;